/**
 * WebSocket hook for real-time alerts.
 * 
 * Handles connection, reconnection, and message parsing.
 * Exposes events through a callback and maintains connection status.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

export interface LiveEvent {
  type: 'stock_trade' | 'bill_introduced' | 'vote_completed' | 'prediction_market' | 'connected' | 'heartbeat' | 'pong'
  title?: string
  message?: string
  data?: Record<string, unknown>
  reference_type?: string
  reference_id?: string
  timestamp?: string
}

export interface UseWebSocketOptions {
  /** Callback when a new event arrives */
  onEvent?: (event: LiveEvent) => void
  /** Whether to auto-connect on mount (default: true) */
  autoConnect?: boolean
  /** Reconnect delay in ms (default: 3000) */
  reconnectDelay?: number
  /** Max reconnect attempts (default: 10) */
  maxReconnectAttempts?: number
  /** User ID for authenticated connections (enables personalized alerts) */
  userId?: string
}

export interface UseWebSocketReturn {
  /** Current connection status */
  status: 'connecting' | 'connected' | 'disconnected' | 'error'
  /** Number of active connections on server (if known) */
  connectionCount: number | null
  /** Manually connect */
  connect: () => void
  /** Manually disconnect */
  disconnect: () => void
  /** Last received event */
  lastEvent: LiveEvent | null
}

// Determine WebSocket URL based on environment
function getWebSocketUrl(userId?: string): string {
  let baseUrl: string
  if (import.meta.env.DEV) {
    // In dev, connect to local backend
    baseUrl = 'ws://localhost:8000/ws/live'
  } else {
    // In production, use the API URL
    const apiUrl = import.meta.env.VITE_API_URL || 'https://backend-polititrack-production.up.railway.app'
    const wsUrl = apiUrl.replace(/^http/, 'ws')
    baseUrl = `${wsUrl}/ws/live`
  }

  // Add user_id if authenticated
  if (userId) {
    return `${baseUrl}?user_id=${userId}`
  }
  return baseUrl
}

export function useWebSocket(options: UseWebSocketOptions = {}): UseWebSocketReturn {
  const {
    onEvent,
    autoConnect = true,
    reconnectDelay = 3000,
    maxReconnectAttempts = 10,
    userId,
  } = options

  const [status, setStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected')
  const [connectionCount, _setConnectionCount] = useState<number | null>(null)
  const [lastEvent, setLastEvent] = useState<LiveEvent | null>(null)

  const wsRef = useRef<WebSocket | null>(null)
  const reconnectAttemptsRef = useRef(0)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current)
      pingIntervalRef.current = null
    }
    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }
  }, [])

  const connect = useCallback(() => {
    cleanup()
    setStatus('connecting')

    try {
      const ws = new WebSocket(getWebSocketUrl(userId))
      wsRef.current = ws

      ws.onopen = () => {
        setStatus('connected')
        reconnectAttemptsRef.current = 0

        // Start ping interval to keep connection alive
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping')
          }
        }, 25000) // Ping every 25s (server heartbeats at 30s)
      }

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as LiveEvent
          setLastEvent(data)

          // Don't notify for internal messages
          if (data.type !== 'heartbeat' && data.type !== 'pong' && data.type !== 'connected') {
            onEvent?.(data)
          }
        } catch (e) {
          console.warn('[WebSocket] Failed to parse message:', event.data)
        }
      }

      ws.onclose = () => {
        setStatus('disconnected')
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current)
          pingIntervalRef.current = null
        }

        // Attempt to reconnect
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current++
          const delay = Math.min(reconnectDelay * reconnectAttemptsRef.current, 30000)
          if (import.meta.env.DEV) {
            console.log(`[WebSocket] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current})`)
          }
          reconnectTimeoutRef.current = setTimeout(connect, delay)
        } else {
          setStatus('error')
          console.warn('[WebSocket] Max reconnect attempts reached')
        }
      }

      ws.onerror = (error) => {
        console.error('[WebSocket] Error:', error)
        // onclose will handle reconnection
      }
    } catch (e) {
      console.error('[WebSocket] Failed to create connection:', e)
      setStatus('error')
    }
  }, [cleanup, onEvent, reconnectDelay, maxReconnectAttempts, userId])

  const disconnect = useCallback(() => {
    reconnectAttemptsRef.current = maxReconnectAttempts // Prevent auto-reconnect
    cleanup()
    setStatus('disconnected')
  }, [cleanup, maxReconnectAttempts])

  // Auto-connect on mount
  useEffect(() => {
    if (autoConnect) {
      connect()
    }
    return () => {
      cleanup()
    }
  }, [autoConnect, connect, cleanup])

  return {
    status,
    connectionCount,
    connect,
    disconnect,
    lastEvent,
  }
}
